import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { supabase } from '../../services/supabase';
import { Deal, FilterOptions } from '../../types';
import { api } from '../../services/api';

// Make all properties optional to handle potentially incomplete data
interface DealsState {
  deals: Partial<Deal>[];
  loading: boolean;
  error: string | null;
  currentDeal: Deal | null;
  totalItems: number;
  totalPages: number;
}

const initialState: DealsState = {
  deals: [],
  loading: false,
  error: null,
  currentDeal: null,
  totalItems: 0,
  totalPages: 1
};

// Async thunks
export const fetchDeals = createAsyncThunk(
  'deals/fetchDeals',
  async (filters: FilterOptions & { page?: number, limit?: number }) => {
    try {
      // Use the API service that properly handles pagination
      const result = await api.getFilteredDeals({
        category: filters.category,
        sort: filters.sort,
        location: filters.location,
        date: filters.date,
        page: filters.page || 1,
        limit: filters.limit || 10
      });
      
      // Return only what the API actually provides
      return {
        deals: result.deals,
        totalPages: Math.max(1, result.totalPages), // Ensure totalPages is at least 1
        totalItems: result.totalItems || 0
      };
    } catch (error) {
      console.error('Error fetching deals:', error);
      throw error;
    }
  }
);

export const fetchDealDetails = createAsyncThunk(
  'deals/fetchDealDetails',
  async (dealId: string) => {
    try {
      // First, fetch the deal details
      const { data: dealData, error: dealError } = await supabase
        .from('deals')
        .select('*')
        .eq('id', dealId)
        .single();
      
      if (dealError) throw dealError;
      if (!dealData) throw new Error('Deal not found');

      // Then, fetch the profile data for the user who created the deal
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('username')
        .eq('id', dealData.user_id)
        .single();

      if (profileError) {
        console.error('Error fetching profile:', profileError);
        // Return deal data even if profile fetch fails
        return dealData as Deal;
      }

      // Combine the deal data with the profile data
      return {
        ...dealData,
        profiles: profileData
      } as Deal;
    } catch (error) {
      console.error('Error in fetchDealDetails:', error);
      throw error;
    }
  }
);

export const voteOnDeal = createAsyncThunk(
  'deals/voteOnDeal',
  async ({ dealId, voteType }: { dealId: string; voteType: boolean }) => {
    try {
      // Use the secure API method that performs server-side validation
      const result = await api.voteDeal(dealId, voteType);
      
      if (!result.success) {
        throw new Error('Voting failed');
      }
      
      // Return the vote counts from the secure API call
      return {
        dealId,
        voteType,
        hotVotes: result.hotVotes,
        coldVotes: result.coldVotes
      };
    } catch (error: any) {
      console.error('Error in voteOnDeal:', error);
      throw error;
    }
  }
);

export const dealsSlice = createSlice({
  name: 'deals',
  initialState,
  reducers: {
    setCurrentDeal: (state, action) => {
      state.currentDeal = action.payload;
    },
    clearCurrentDeal: (state) => {
      state.currentDeal = null;
    },
    updateVoteCounts: (state, action) => {
      const { dealId, hotVotes, coldVotes } = action.payload;
      // Update in the deals array
      const deal = state.deals.find(d => d.id === dealId);
      if (deal) {
        deal.hot_votes = hotVotes;
        deal.cold_votes = coldVotes;
      }
      // Update in currentDeal if it matches
      if (state.currentDeal && state.currentDeal.id === dealId) {
        state.currentDeal.hot_votes = hotVotes;
        state.currentDeal.cold_votes = coldVotes;
      }
    },
    updateTemperatureOffset: (state, action) => {
      const { dealId, offsetValue } = action.payload;
      // Update in the deals array
      const deal = state.deals.find(d => d.id === dealId);
      if (deal) {
        deal.temperature_offset = offsetValue;
      }
      // Update in currentDeal if it matches
      if (state.currentDeal && state.currentDeal.id === dealId) {
        state.currentDeal.temperature_offset = offsetValue;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Deals
      .addCase(fetchDeals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeals.fulfilled, (state, action) => {
        state.loading = false;
        // Explicitly cast deals to the expected type
        state.deals = action.payload.deals as Partial<Deal>[];
        // Update pagination data
        state.totalPages = action.payload.totalPages;
        state.totalItems = action.payload.totalItems;
      })
      .addCase(fetchDeals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch deals';
      })
      // Fetch Deal Details
      .addCase(fetchDealDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDealDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.currentDeal = action.payload;
      })
      .addCase(fetchDealDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Failed to fetch deal details';
      })
      // Vote on Deal
      .addCase(voteOnDeal.fulfilled, (state, action) => {
        const { dealId, voteType, hotVotes, coldVotes } = action.payload;
        const deal = state.deals.find(d => d.id === dealId);
        if (deal) {
          if (voteType) {
            deal.hot_votes = hotVotes;
          } else {
            deal.cold_votes = coldVotes;
          }
        }
        if (state.currentDeal?.id === dealId) {
          if (voteType) {
            state.currentDeal.hot_votes = hotVotes;
          } else {
            state.currentDeal.cold_votes = coldVotes;
          }
        }
      })
  }
});

export const { setCurrentDeal, clearCurrentDeal, updateVoteCounts, updateTemperatureOffset } = dealsSlice.actions;
export default dealsSlice.reducer; 